import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ShoppingBagIcon, RotateCcwIcon, LayersIcon, ChevronRightIcon, ChevronLeftIcon, MicIcon, GlobeIcon } from 'lucide-react';
import { DeckAmbience } from '../SlideFrame';
import { DeckLogoPlate } from '../DeckLogo';
import { BrowserFrame } from '../BrowserFrame';
import { Cursor } from '../Cursor';
import { ApparelShopPage, NoirWordmark } from '../ApparelShopPage';
import { TalabAskBar, TalabUserBubble, TalabAgentBubble, TalabTypingRow } from '../TalabWindow';
import { TalabResultCard } from '../TalabResultCard';
import { VoiceCapture } from '../VoiceCapture';
import { LanguageMarquee } from '../LanguageMarquee';
import { DENIM_RESULTS, JEANS_RESULTS, PAIRING_RESULTS, LIGHTER_SHIRT_RESULTS } from '../../../data/deck';
import { useTypewriter } from '../../../hooks/useTypewriter';
import { useSequence } from '../../../hooks/useSequence';
const EASE = [0.23, 1, 0.32, 1] as const;
const PROMPT = 'I’m looking for black jeans, size 34.';
/** Spoken in Arabic: "I want a lighter colour than this shirt." */
const VOICE_PROMPT = 'ابغى لون فاتح من هذا القميص';
const CAPABILITIES = ['Guides discovery', 'Answers questions', 'Recommends products', 'Upsells and cross-sells', 'Speaks every language'];

/**
 * The demo renders at a larger logical size and is scaled down, so the page
 * reads like a real browser window rather than a magnified mock.
 */
const DEMO_ZOOM = 0.86;
const DENIM_CARD_WIDTH = 220;
/** One carousel page of the colourway range. */
const DENIM_SCROLL = 480;
interface SlideSalesAgentProps {
  active: boolean;
  /** Lets the demo hide the deck's own chrome while it owns the screen. */
  onImmersiveChange?: (immersive: boolean) => void;
}
export function SlideSalesAgent({
  active,
  onImmersiveChange
}: SlideSalesAgentProps) {
  /**
   * 0      title
   * 1      shop goes full screen and the customer browses it end to end
   * 2      page glides back to the top, cursor reaches for the ribbon
   * 3      Denim is selected — the full colourway range answers in the chat
   * 4      the customer scrolls the range once
   * 5–6    then asks for something specific, typed live
   * 7–11   matches, a pick added to cart, then the styling cross-sell
   * 12–14  the customer speaks in Arabic; Talab transcribes, answers in
   *        Arabic and offers the shirt in brighter colourways
   * 15     the demo zooms back into its frame and the slide returns
   */
  const step = useSequence([2200, 12600, 1800, 1700, 1900, 1200, 3000, 1300, 1700, 1800, 2600, 2400, 3900, 1600, 3200], active);
  const {
    typed,
    done
  } = useTypewriter(PROMPT, {
    active: step >= 6,
    startDelay: 300,
    speed: 36
  });
  const {
    typed: spoken,
    done: spokenDone
  } = useTypewriter(VOICE_PROMPT, {
    active: step >= 12,
    startDelay: 1200,
    speed: 115
  });
  const listening = step === 12;
  const ended = step >= 15;
  const fullScreen = step >= 1 && !ended;
  const inChat = step >= 3;
  const scrolledRange = step >= 4;
  React.useEffect(() => {
    onImmersiveChange?.(fullScreen);
    return () => onImmersiveChange?.(false);
  }, [fullScreen, onImmersiveChange]);

  // Slide-space positions for the zoomed-down page beneath.
  const cursor = step >= 11 ? {
    x: 1196,
    y: 682
  } : step >= 5 ? {
    x: 618,
    y: 682
  } : step >= 4 ? {
    x: 1243,
    y: 538
  } : step >= 2 ? {
    x: 539,
    y: 300
  } : {
    x: 900,
    y: 430
  };
  return <div className="relative w-full h-full overflow-hidden bg-white" data-id="element-2219" data-mp="x1oh3equadsu">
      <DeckAmbience data-id="element-2220" data-mp="s0qy4zy09fqa" />

      {/* Title first — and again at the end, once the demo has made its point */}
      <motion.div className="absolute left-14 right-14 top-10 z-10" animate={{
      opacity: fullScreen ? 0 : 1,
      y: fullScreen ? -14 : 0
    }} transition={{
      duration: 0.7,
      ease: EASE
    }} data-id="element-2221" data-mp="n2yxnhjaolah">
        <div className="flex items-center gap-3" data-id="element-2222" data-mp="37rdbu3lunfga">
          <DeckLogoPlate data-id="element-2223" data-mp="10h7w4tz9zkim" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400" data-id="element-2224" data-mp="10gog5it6r678">
            More than a storefront
          </span>
        </div>
        <h2 className="mt-5 font-display font-bold text-[40px] leading-[1.08] tracking-tight text-dark-navy" data-id="element-2225" data-mp="159ra3s1vkhny">
          Not just your storefront. Your AI sales agent.
        </h2>
        <div className="flex items-center gap-2 mt-4" data-id="element-2226" data-mp="viei90g2trhx">
          {CAPABILITIES.map((capability, index) => <motion.span key={capability} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-100 shadow-sm text-[11px] text-slate-600" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45,
          ease: EASE,
          delay: 0.25 + index * 0.08
        }} data-id="element-2227" data-mp="1j5mktam4emg9" data-mpi={index}>
              <CheckIcon className="w-3 h-3 text-primary-indigo" strokeWidth={3} data-id="element-2228" data-mp="3u1v5ujmost5a" />
              {capability}
            </motion.span>)}
        </div>
        <motion.div animate={{
        opacity: ended ? 1 : 0
      }} transition={{
        duration: 0.7,
        ease: EASE,
        delay: ended ? 0.5 : 0
      }} data-id="element-2229" data-mp="2h05pmb87wteg">
          <p className="mt-3.5 text-[13.5px] text-slate-500" data-id="element-2230" data-mp="2c4hy3p69jo5s">
            Talab helps businesses do more than show products —{' '}
            <span className="font-semibold text-dark-navy" data-id="element-2231" data-mp="2m01usdsniqvl">it helps them sell better.</span>{' '}
            And it does it in whatever language — typed or spoken — the customer walks in with.
          </p>
          <LanguageMarquee className="mt-3 h-6" data-id="element-2232" data-mp="1tcfhuexja9w2" />
        </motion.div>
      </motion.div>

      {/* The merchant's storefront — expands to fill the slide, then returns */}
      <motion.div className="absolute overflow-hidden" initial={false} animate={fullScreen ? {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: 0
    } : {
      top: 268,
      left: 56,
      right: 56,
      bottom: 48,
      borderRadius: 18
    }} transition={{
      duration: ended ? 1.5 : 1.1,
      ease: EASE
    }} style={{
      boxShadow: '0 30px 80px -30px rgba(11,18,32,0.35)'
    }} data-id="element-2233" data-mp="22cc7ojvntesp">
        <BrowserFrame url="talab.app/shop/noir" bare zoom={fullScreen ? DEMO_ZOOM : 1} className="h-full" data-id="element-2234" data-mp="3enxygq38b59s">
          <AnimatePresence mode="wait" data-id="element-2235" data-mp="3ji29pljj1zgo">
            {inChat ? <motion.div key="chat" className="absolute inset-0 flex flex-col bg-white" initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: EASE
          }} data-id="element-2236" data-mp="3oe9h7irknj0q">
                <div className="flex items-center justify-between h-11 px-6 shrink-0 border-b border-slate-100" data-id="element-2237" data-mp="30nn6tku3hmnk">
                  <div className="flex items-center gap-3" data-id="element-2238" data-mp="35pmdp4p1n1p9">
                    <NoirWordmark size="sm" data-id="element-2239" data-mp="fzsegqkba112" />
                    <span className="w-px h-4 bg-slate-200" data-id="element-2240" data-mp="g13hgo5xn4r4" />
                    <span className="flex items-center gap-1.5 text-[10px] text-slate-400" data-id="element-2241" data-mp="kx11p5kbgv96">
                      <span className="w-1.5 h-1.5 rounded-full bg-success" data-id="element-2242" data-mp="25f342v6kivue" />
                      AI stylist · Online
                    </span>
                  </div>
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-slate-200 text-[9.5px] font-medium text-slate-500" data-id="element-2243" data-mp="1vq1qfbnhwqjl">
                    <RotateCcwIcon className="w-2.5 h-2.5" data-id="element-2244" data-mp="1qqp3ys0jdlhj" /> New request
                  </span>
                </div>

                <div className="relative flex-1 min-h-0 flex flex-col justify-end gap-3.5 overflow-hidden px-8 py-5" data-id="element-2245" data-mp="1lq11na6uxi80">
                  {/* The category the customer tapped enters the conversation */}
                  <div className="flex justify-end" data-id="element-2246" data-mp="1ctjs2be55sed">
                    <motion.span className="flex items-center gap-1.5 px-4 py-2 rounded-2xl rounded-tr-sm bg-slate-100 text-[12.5px] text-dark-navy" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.4,
                  ease: EASE
                }} data-id="element-2247" data-mp="17te69bujz047">
                      <LayersIcon className="w-3.5 h-3.5 text-primary-indigo" data-id="element-2248" data-mp="2g04gq9iqobgl" />
                      Denim
                    </motion.span>
                  </div>

                  <TalabAgentBubble delay={0.35} data-id="element-2249" data-mp="21ayc2614tt1b">
                    Here&apos;s the full <span className="font-semibold text-dark-navy" data-id="element-2250" data-mp="2699mahvca5k2">Denim</span>{' '}
                    range — 24 pieces across black, indigo, light wash, charcoal and stone. Scroll
                    through, or tell me your size and the fit you want.
                  </TalabAgentBubble>

                  {/* Colourway range — scrolls horizontally, as in the product */}
                  <motion.div className="relative pl-[40px]" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.4,
                delay: 0.5
              }} data-id="element-2251" data-mp="1naxbkros4nmd">
                    <div className="overflow-hidden" data-id="element-2252" data-mp="3hndp8djwn419">
                      <motion.div className="flex gap-3" animate={{
                    x: scrolledRange ? -DENIM_SCROLL : 0
                  }} transition={{
                    duration: 1.2,
                    ease: EASE
                  }} data-id="element-2253" data-mp="161cynd0i4hqy">
                        {DENIM_RESULTS.map((result, index) => <TalabResultCard key={result.id} result={result} width={DENIM_CARD_WIDTH} delay={0.6 + index * 0.07} data-id="element-2254" data-mp="1b0fq1unk6pgg" data-mpi={index} />)}
                      </motion.div>
                    </div>

                    {/* Edge fade, so the range reads as continuing past the frame */}
                    <div className="absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-white to-transparent pointer-events-none" data-id="element-2255" data-mp="1tpw5do3wkmgx" />

                    {/* Carousel controls — the right arrow stays, there is always
                        more of the range to scroll into. */}
                    <motion.span className="absolute right-1 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-primary-indigo ring-2 ring-white shadow-large flex items-center justify-center" initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.4,
                  ease: EASE,
                  delay: 0.7
                }} data-id="element-2256" data-mp="10zr7hx0l2j86">
                      <ChevronRightIcon className="w-5 h-5 text-white" data-id="element-2257" data-mp="1eq4jqbkick3b" />
                    </motion.span>
                    <motion.span className="absolute left-[46px] top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-large flex items-center justify-center" animate={{
                  opacity: scrolledRange ? 1 : 0,
                  scale: scrolledRange ? 1 : 0.9
                }} transition={{
                  duration: 0.4,
                  ease: EASE,
                  delay: scrolledRange ? 0.6 : 0
                }} data-id="element-2258" data-mp="19ymgfgv0i7f6">
                      <ChevronLeftIcon className="w-5 h-5 text-slate-500" data-id="element-2259" data-mp="3l9rx9lh4t26e" />
                    </motion.span>
                  </motion.div>

                  {done && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.35,
                ease: EASE
              }} data-id="element-2260" data-mp="3ub89lyribed9">
                      <TalabUserBubble data-id="element-2261" data-mp="838c5vm77rvp">{PROMPT}</TalabUserBubble>
                    </motion.div>}

                  {step === 7 && <TalabTypingRow data-id="element-2262" data-mp="cv9unz9ymeu6" />}

                  {step >= 8 && <motion.div className="space-y-3.5" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.45,
                ease: EASE
              }} data-id="element-2263" data-mp="gva8bhp8im06">
                      <TalabAgentBubble data-id="element-2264" data-mp="2mxe80et76vwo">
                        Two black jeans in size 34 are in stock now — both slim through the leg,
                        and the first is our best seller.
                      </TalabAgentBubble>
                      <div className="flex gap-3 pl-[40px]" data-id="element-2265" data-mp="2wo8vtnm3e3k8">
                        {JEANS_RESULTS.map((result, index) => <TalabResultCard key={result.id} result={result} width={196} delay={index * 0.09} selected={step >= 9 && index === 0} data-id="element-2266" data-mp="2runvhox10xhv" data-mpi={index} />)}
                        {step >= 11 && PAIRING_RESULTS.map(result => <TalabResultCard key={result.id} result={result} width={196} delay={0.1} highlight data-id="element-2267" data-mp="1l7sefoqz9cuq" />)}
                      </div>
                      {step >= 10 && <TalabAgentBubble delay={0.05} data-id="element-2268" data-mp="3i7121dhylr6a">
                          Added to your cart. Customers who bought these jeans often pair them
                          with our charcoal Oxford shirt — want me to add it for{' '}
                          <span className="font-semibold text-dark-navy" data-id="element-2269" data-mp="3959xwd32hsej">AED 179</span>?
                        </TalabAgentBubble>}
                    </motion.div>}

                  {/* Spoken in Arabic — Talab answers in the same language */}
                  {step >= 13 && <motion.div className="flex justify-end" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.35,
                ease: EASE
              }} data-id="element-2270" data-mp="3478grxp7c35p">
                      <span className="flex items-center gap-2 max-w-[60%] px-4 py-2.5 rounded-2xl rounded-tr-sm bg-primary-indigo text-white text-[13px]" dir="rtl" data-id="element-2271" data-mp="2yhgmmwkrzyhd">
                        <MicIcon className="w-3.5 h-3.5 text-white/70 shrink-0" data-id="element-2272" data-mp="2pjwhvdnpfk20" />
                        {VOICE_PROMPT}
                      </span>
                    </motion.div>}

                  {step === 13 && <TalabTypingRow data-id="element-2273" data-mp="2atfidzumr47r" />}

                  {step >= 14 && <motion.div className="space-y-3.5" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.45,
                ease: EASE
              }} data-id="element-2274" data-mp="2ar2rgx6w9jik">
                      <TalabAgentBubble rtl data-id="element-2275" data-mp="1wswcay9q6888">
                        أكيد — نفس القميص متوفر بألوان أفتح ومقاس 34. هذي أربع خيارات فاتحة
                        تنسجم مع الجينز الأسود.
                      </TalabAgentBubble>
                      <div className="flex gap-3 pl-[40px]" dir="rtl" data-id="element-2276" data-mp="1hsh6icv9r11w">
                        {LIGHTER_SHIRT_RESULTS.map((result, index) => <TalabResultCard key={result.id} result={result} width={186} delay={index * 0.09} rtl data-id="element-2277" data-mp="1roaq2g7enut6" data-mpi={index} />)}
                      </div>
                    </motion.div>}
                </div>

                <div className="shrink-0 px-8 pb-5" data-id="element-2278" data-mp="18x1dtz692rxw">
                  {step === 12 ? <VoiceCapture transcript={spoken} listening={!spokenDone} rtl data-id="element-2279" data-mp="1dp1758rtemli" /> : <TalabAskBar compact placeholder="Ask NOIR anything..." text={done && step < 12 ? '' : step >= 13 ? '' : typed} caret={step >= 5 && !done} focused={step >= 5 && !done} data-id="element-2280" data-mp="g8cb5vpjqek1" />}
                </div>

                <AnimatePresence data-id="element-2281" data-mp="mxlwyb30y2e6">
                  {step >= 9 && !ended && <motion.div className="absolute top-16 right-8 flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-dark-navy text-white shadow-large" initial={{
                opacity: 0,
                y: -10,
                scale: 0.97
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.28,
                ease: EASE
              }} data-id="element-2282" data-mp="mzojum8vrfeo">
                      <ShoppingBagIcon className="w-3.5 h-3.5" data-id="element-2283" data-mp="3vd5lwxwnwynf" />
                      <span className="text-[11.5px] font-medium" data-id="element-2284" data-mp="1fm9dxugqq5lx">
                        Slim Fit Black Jeans · Size 34 added
                      </span>
                    </motion.div>}
                </AnimatePresence>
              </motion.div> : <motion.div key="storefront" className="absolute inset-0" initial={{
            opacity: 1
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.5,
            ease: EASE
          }} data-id="element-2285" data-mp="1i8s4wtpyywme">
                <ApparelShopPage mode={step === 1 ? 'browse' : 'top'} browseDuration={10.4} browseDelay={1.3} activeCategory={step >= 2 ? 'Denim' : undefined} data-id="element-2286" data-mp="1d8dyth7xhwr0" />
              </motion.div>}
          </AnimatePresence>
        </BrowserFrame>
      </motion.div>

      {/* Names the multilingual moment while the Arabic exchange is on screen */}
      <AnimatePresence data-id="element-2287" data-mp="1w442d8vif170">
        {step >= 12 && !ended && <motion.div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2.5 px-4 py-2 rounded-full bg-dark-navy/90 backdrop-blur-sm shadow-large" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 12
      }} transition={{
        duration: 0.4,
        ease: EASE
      }} data-id="element-2288" data-mp="2jv9jdrd6rziw">
            <GlobeIcon className="w-3.5 h-3.5 text-electric-cyan shrink-0" data-id="element-2289" data-mp="1q7demul66b2p" />
            <span className="text-[11.5px] text-white/85" data-id="element-2290" data-mp="1l7r0mixioqsu">
              The customer switched to Arabic, by voice —{' '}
              <span className="font-semibold text-white" data-id="element-2291" data-mp="2ity2vm6cm721">
                Talab answers in any language, typed or spoken.
              </span>
            </span>
          </motion.div>}
      </AnimatePresence>

      {/* Tells the room what they're looking at while the demo owns the screen */}
      <motion.div className="absolute top-2 right-4 z-30 flex items-center gap-2 px-3 py-1 rounded-full bg-dark-navy/85 backdrop-blur-sm" animate={{
      opacity: fullScreen ? 1 : 0,
      y: fullScreen ? 0 : -6
    }} transition={{
      duration: 0.5,
      ease: EASE,
      delay: fullScreen ? 0.9 : 0
    }} data-id="element-2292" data-mp="23wp261v5nqxn">
        <motion.span className="w-1.5 h-1.5 rounded-full bg-electric-cyan" animate={{
        opacity: [1, 0.35, 1]
      }} transition={{
        duration: 2.2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2293" data-mp="34788o2ic3goa" />
        <span className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-white/80" data-id="element-2294" data-mp="3e2rwe1mqv91y">
          Demo mode · NOIR apparel storefront
        </span>
      </motion.div>

      <Cursor x={cursor.x} y={cursor.y} clickId={step >= 12 ? 12 : step >= 5 ? 5 : step === 4 ? 4 : step === 3 ? 3 : 0} visible={step >= 1 && (step < 7 || step === 11 || step === 12)} data-id="element-2295" data-mp="3nt4ihya7jaa7" />
    </div>;
}